import { useState } from 'react';
import { Rss, Check, Shield, Server, Users, Zap } from 'lucide-react';

export default function Buy() {
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');

  const handlePurchase = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/stripe/create-checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await response.json();

      if (data.url) {
        window.location.href = data.url;
      } else {
        alert('Failed to create checkout session');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      alert('Failed to start checkout process');
    } finally {
      setLoading(false);
    }
  };

  const features = [
    { icon: Rss, title: 'RSS Reader', description: 'Subscribe to any RSS/Atom feed' },
    { icon: Users, title: 'Social Features', description: 'Share, comment, and discuss articles' },
    { icon: Zap, title: 'AI Digests', description: 'Weekly AI-generated summaries' },
    { icon: Server, title: 'Self-Hosted', description: 'Full control of your data' },
    { icon: Shield, title: 'Privacy First', description: 'No tracking, no ads' },
  ];

  const included = [
    'Unlimited feeds and articles',
    'Social sharing and comments',
    'AI-powered weekly digests',
    'Mobile-friendly interface',
    'iOS app support',
    'Browser extension',
    'OPML import/export',
    'Folder organization',
    'Docker deployment',
    'SQLite or PostgreSQL',
    'Email notifications',
    'Lifetime updates',
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-canvas border-b border-edge">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-accent-600 rounded-lg flex items-center justify-center">
              <Rss className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-ink">OurSS</h1>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-ink mb-6">
            Your Self-Hosted RSS Reader
          </h2>
          <p className="text-xl text-ink-muted mb-8 max-w-2xl mx-auto">
            A modern, social RSS reader you can host on your own server.
            No subscriptions, no tracking, just reading.
          </p>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 bg-canvas">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
            {features.map((feature) => (
              <div key={feature.title} className="text-center p-4">
                <div className="w-12 h-12 bg-accent-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                  <feature.icon className="w-6 h-6 text-accent-600" />
                </div>
                <h3 className="font-semibold text-ink mb-1">{feature.title}</h3>
                <p className="text-sm text-ink-muted">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-xl mx-auto px-4">
          <div className="bg-canvas rounded-2xl shadow-lg border border-edge overflow-hidden">
            <div className="p-8 text-center border-b border-gray-100">
              <h3 className="text-2xl font-bold text-ink mb-2">OurSS License</h3>
              <div className="flex items-baseline justify-center gap-1 mb-2">
                <span className="text-5xl font-bold text-ink">$99</span>
                <span className="text-ink-muted">one-time</span>
              </div>
              <p className="text-ink-muted">No subscriptions, no recurring fees</p>
            </div>

            <div className="p-8">
              <div className="space-y-3 mb-8">
                {included.map((item) => (
                  <div key={item} className="flex items-center gap-3">
                    <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span className="text-ink-secondary">{item}</span>
                  </div>
                ))}
              </div>

              <div className="space-y-4">
                <input
                  type="email"
                  placeholder="Your email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-dark-border bg-canvas text-ink rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-accent-500 transition-colors duration-100"
                />
                <button
                  onClick={handlePurchase}
                  disabled={loading || !email}
                  className="w-full py-4 btn-golden golden-effect rounded-pill disabled:bg-gray-300 disabled:text-gray-500 disabled:shadow-none font-semibold transition-all duration-100 animate-glow"
                >
                  {loading ? 'Redirecting...' : 'Purchase License'}
                </button>
              </div>

              <p className="text-center text-sm text-ink-muted mt-4">
                Secure checkout powered by Stripe
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-canvas">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-ink mb-8 text-center">
            Frequently Asked Questions
          </h2>
          <div className="space-y-6">
            <div>
              <h3 className="font-semibold text-ink mb-2">How many servers can I use this on?</h3>
              <p className="text-ink-muted">Each license is valid for 1 server installation. Need more? Contact us for volume pricing.</p>
            </div>
            <div>
              <h3 className="font-semibold text-ink mb-2">What are the server requirements?</h3>
              <p className="text-ink-muted">OurSS runs on any server with Docker or Node.js 20+. 512MB RAM minimum, 1GB recommended.</p>
            </div>
            <div>
              <h3 className="font-semibold text-ink mb-2">How do updates work?</h3>
              <p className="text-ink-muted">Lifetime updates included. Download new versions from your license page anytime.</p>
            </div>
            <div>
              <h3 className="font-semibold text-ink mb-2">Do I need the AI digests feature?</h3>
              <p className="text-ink-muted">No, it's optional. AI digests require an OpenAI API key but everything else works without it.</p>
            </div>
            <div>
              <h3 className="font-semibold text-ink mb-2">Is there a refund policy?</h3>
              <p className="text-ink-muted">Yes, 30-day money-back guarantee if OurSS doesn't meet your needs.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-gray-900 text-gray-400">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <p>&copy; {new Date().getFullYear()} OurSS. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
